import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlusCircle, CalendarDays, MapPin } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { Loader } from "@/components/common/Loader";
import { PageHeader, StatusBadge, EmptyState } from "@/components/dashboard/ui";

export default function Events() {
  const [events, setEvents] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/events").then((r) => setEvents(r.data.events)).catch(() => setEvents([])); }, []);
  if (!events) return <Loader />;

  return (
    <div>
      <PageHeader title="My Events" subtitle={`${events.length} event${events.length !== 1 ? "s" : ""} in your studio`}
        action={<button data-testid="new-event-btn" onClick={() => navigate("/app/events/new")} className="inline-flex items-center gap-2 bg-gold text-black font-medium rounded-full px-5 py-2.5 hover:bg-gold-light transition-colors"><PlusCircle className="w-4 h-4" /> Create Event</button>} />

      {events.length === 0 ? (
        <EmptyState icon={CalendarDays} title="No events yet" desc="Create your first event to start uploading photos and sharing galleries."
          action={<button onClick={() => navigate("/app/events/new")} className="bg-gold text-black rounded-full px-6 py-2.5 font-medium">Create Event</button>} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {events.map((e) => {
            const pct = e.photo_count ? Math.round((e.selected_count / e.photo_count) * 100) : 0;
            return (
              <Link key={e.id} to={`/app/events/${e.id}`} data-testid={`event-card-${e.id}`} className="group rounded-2xl overflow-hidden border border-border bg-black/40 hover:border-gold/30 transition-colors hover-lift">
                <div className="relative aspect-[16/10] bg-secondary overflow-hidden">
                  {e.cover_image && <img src={mediaUrl(e.cover_image)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute top-3 left-3"><span className="text-[11px] glass-dark rounded-full px-2.5 py-1 text-gold">{e.event_type}</span></div>
                  <div className="absolute bottom-3 left-4 right-4">
                    <div className="text-white font-medium text-lg leading-tight">{e.name}</div>
                    <div className="flex items-center gap-3 text-xs text-white/70 mt-1">
                      <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{e.event_date}</span>
                      {e.venue && <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3" />{e.venue}</span>}
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-secondary-foreground">{e.photo_count} photos · {e.selected_count} selected</span>
                    <StatusBadge status={e.selection_status} />
                  </div>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mt-3"><div className="h-full bg-gold" style={{ width: `${pct}%` }} /></div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
